import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import "./ConsentBanner.css";
import { consentChoice, consentRequired, recordConsent } from "./consentState.ts";
import { useTranslation } from "../i18n/useTranslation.ts";

/**
 * The analytics consent banner. ADR 0025, second amendment.
 *
 * Renders only where consent is required and has not been given - which on
 * this site means a European visitor who has not answered. Everyone else gets
 * nothing at all, not a dismissed banner or a hidden one: `consentRequired()`
 * is false and this returns null on the first render (§C).
 *
 * TWO EQUAL BUTTONS, and the equality is the requirement rather than a
 * courtesy - refusing has to be as easy as accepting, and "Accept" beside a
 * dismissal X is the specific pattern regulators name. They are the same
 * element, the same size and the same weight; neither is styled as primary.
 * Resist the design instinct to make one of them the obvious one.
 *
 * There are no per-purpose toggles because there is exactly one purpose. A
 * preferences panel over a single checkbox would be theatre.
 *
 * It sits at the bottom and does not trap focus or block the page. People
 * arrive here to print one worksheet, and a modal between them and that - on a
 * site with no ads and one analytics cookie - would cost more than it protects.
 */
export function ConsentBanner() {
    const t = useTranslation();
    // Read once on mount. These come from index.html and cannot change under
    // us; re-reading on every render would only invite a loop.
    const [answered, setAnswered] = useState(() => consentChoice() !== null);
    const [required] = useState(() => consentRequired());
    const banner = useRef<HTMLDivElement>(null);
    const showing = required && !answered;

    /*
     * A fixed bar covers whatever is at the bottom of the page, and on a
     * generator page at a laptop height that is the download button - the one
     * thing the visit is for. ADR 0025's preserved note about where the ad unit
     * was allowed to sit says the same thing about the same button.
     *
     * So the body gets padding equal to the banner's real measured height
     * while it is up, and loses it the moment it is answered. Measured rather
     * than hard-coded because the message wraps to two, three or four lines
     * depending on width and locale - a constant here would be right at one
     * viewport and wrong at the rest.
     */
    useEffect(() => {
        if (!showing) {
            return;
        }

        const apply = () => {
            const height = banner.current?.offsetHeight;
            if (height) {
                document.body.style.paddingBottom = `${height}px`;
            }
        };

        apply();
        window.addEventListener("resize", apply);
        return () => {
            window.removeEventListener("resize", apply);
            document.body.style.paddingBottom = "";
        };
    }, [showing]);

    if (!showing) {
        return null;
    }

    const answer = (choice: "granted" | "denied") => {
        recordConsent(choice);
        setAnswered(true);
    };

    return (
        <div
            className="consentBanner"
            role="region"
            aria-label={t("consent.ariaLabel")}
            ref={banner}
        >
            <div className="consentBannerInner">
                <p className="consentMessage">
                    {t("consent.message")}{" "}
                    <Link to="/privacy">{t("consent.learnMore")}</Link>
                </p>
                <div className="consentActions">
                    <button type="button" className="consentButton" onClick={() => answer("denied")}>
                        {t("consent.reject")}
                    </button>
                    <button type="button" className="consentButton" onClick={() => answer("granted")}>
                        {t("consent.accept")}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default ConsentBanner;
